import React, { useState } from 'react';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [settings, setSettings] = useState({
    // Profile Settings
    profile: {
      visibility: 'public',
      emailNotifications: true,
      smsNotifications: false,
      jobAlerts: true,
      recruiterContact: true
    },
    // Account Settings
    account: {
      email: 'john.doe@example.com',
      phone: '+1 (555) 123-4567',
      language: 'english',
      timezone: 'UTC-5',
      currency: 'USD'
    },
    // Privacy Settings
    privacy: {
      profileVisibility: 'public',
      resumeVisibility: 'employers',
      searchAppearance: true,
      dataSharing: false,
      autoDeleteData: false
    },
    // Notification Settings
    notifications: {
      email: {
        jobMatches: true,
        applicationUpdates: true,
        interviewInvites: true,
        messages: true,
        newsletter: false
      },
      push: {
        jobMatches: true,
        applicationUpdates: true,
        interviewInvites: true,
        messages: true
      },
      sms: {
        interviewReminders: true,
        importantUpdates: false
      }
    },
    // Application Settings
    application: {
      autoSave: true,
      defaultResume: 'primary',
      coverLetterTemplate: 'professional',
      quickApply: false,
      followUpReminders: true
    }
  });

  const handleSettingChange = (category, field, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
  };

  const handleNestedSettingChange = (category, subcategory, field, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [subcategory]: {
          ...prev[category][subcategory],
          [field]: value
        }
      }
    }));
  };

  const settingTabs = [
    { id: 'profile', name: 'Profile', icon: 'fas fa-user' },
    { id: 'account', name: 'Account', icon: 'fas fa-cog' },
    { id: 'privacy', name: 'Privacy', icon: 'fas fa-shield-alt' },
    { id: 'notifications', name: 'Notifications', icon: 'fas fa-bell' },
    { id: 'application', name: 'Application', icon: 'fas fa-edit' },
    { id: 'security', name: 'Security', icon: 'fas fa-lock' }
  ];

  const languages = [
    { value: 'english', label: 'English' },
    { value: 'spanish', label: 'Spanish' },
    { value: 'french', label: 'French' },
    { value: 'german', label: 'German' }
  ];

  const timezones = [
    { value: 'UTC-5', label: 'EST (UTC-5)' },
    { value: 'UTC-6', label: 'CST (UTC-6)' },
    { value: 'UTC-7', label: 'MST (UTC-7)' },
    { value: 'UTC-8', label: 'PST (UTC-8)' },
    { value: 'UTC+0', label: 'GMT (UTC+0)' }
  ];

  const currencies = [
    { value: 'USD', label: 'US Dollar ($)' },
    { value: 'EUR', label: 'Euro (€)' },
    { value: 'GBP', label: 'British Pound (£)' },
    { value: 'CAD', label: 'Canadian Dollar (C$)' }
  ];

  const visibilityOptions = [
    { value: 'public', label: 'Public', description: 'Visible to everyone' },
    { value: 'employers', label: 'Employers Only', description: 'Visible only to verified employers' },
    { value: 'private', label: 'Private', description: 'Only visible to you' }
  ];

  const renderProfileSettings = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Profile Visibility</h3>
        <div className="space-y-4">
          {visibilityOptions.map(option => (
            <label key={option.value} className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
              <input
                type="radio"
                name="profileVisibility"
                value={option.value}
                checked={settings.profile.visibility === option.value}
                onChange={(e) => handleSettingChange('profile', 'visibility', e.target.value)}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="font-medium text-gray-800">{option.label}</div>
                <div className="text-sm text-gray-600">{option.description}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Communication Preferences</h3>
        <div className="space-y-4">
          <label className="flex items-center justify-between">
            <div>
              <div className="font-medium text-gray-800">Email Notifications</div>
              <div className="text-sm text-gray-600">Receive updates via email</div>
            </div>
            <input
              type="checkbox"
              checked={settings.profile.emailNotifications}
              onChange={(e) => handleSettingChange('profile', 'emailNotifications', e.target.checked)}
              className="rounded border-gray-300 text-blue-500 focus:ring-blue-500"
            />
          </label>

          <label className="flex items-center justify-between">
            <div>
              <div className="font-medium text-gray-800">SMS Notifications</div>
              <div className="text-sm text-gray-600">Receive important updates via SMS</div>
            </div>
            <input
              type="checkbox"
              checked={settings.profile.smsNotifications}
              onChange={(e) => handleSettingChange('profile', 'smsNotifications', e.target.checked)}
              className="rounded border-gray-300 text-blue-500 focus:ring-blue-500"
            />
          </label>

          <label className="flex items-center justify-between">
            <div>
              <div className="font-medium text-gray-800">Job Alert Emails</div>
              <div className="text-sm text-gray-600">Get notified about new job matches</div>
            </div>
            <input
              type="checkbox"
              checked={settings.profile.jobAlerts}
              onChange={(e) => handleSettingChange('profile', 'jobAlerts', e.target.checked)}
              className="rounded border-gray-300 text-blue-500 focus:ring-blue-500"
            />
          </label>

          <label className="flex items-center justify-between">
            <div>
              <div className="font-medium text-gray-800">Recruiter Contact</div>
              <div className="text-sm text-gray-600">Allow recruiters to contact you</div>
            </div>
            <input
              type="checkbox"
              checked={settings.profile.recruiterContact}
              onChange={(e) => handleSettingChange('profile', 'recruiterContact', e.target.checked)}
              className="rounded border-gray-300 text-blue-500 focus:ring-blue-500"
            />
          </label>
        </div>
      </div>
    </div>
  );

  const renderAccountSettings = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Account Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
            <input
              type="email"
              value={settings.account.email}
              onChange={(e) => handleSettingChange('account', 'email', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
            <input
              type="tel"
              value={settings.account.phone}
              onChange={(e) => handleSettingChange('account', 'phone', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
            <select
              value={settings.account.language}
              onChange={(e) => handleSettingChange('account', 'language', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {languages.map(lang => (
                <option key={lang.value} value={lang.value}>{lang.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
            <select
              value={settings.account.timezone}
              onChange={(e) => handleSettingChange('account', 'timezone', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {timezones.map(tz => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
            <select
              value={settings.account.currency}
              onChange={(e) => handleSettingChange('account', 'currency', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {currencies.map(currency => (
                <option key={currency.value} value={currency.value}>{currency.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Account Actions</h3>
        <div className="space-y-3">
          <button className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-gray-800">Download My Data</div>
                <div className="text-sm text-gray-600">Export all your personal data</div>
              </div>
              <i className="fas fa-download text-gray-400"></i>
            </div>
          </button>

          <button className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-gray-800">Deactivate Account</div>
                <div className="text-sm text-gray-600">Temporarily disable your account</div>
              </div>
              <i className="fas fa-user-slash text-gray-400"></i>
            </div>
          </button>

          <button className="w-full text-left p-3 border border-red-200 rounded-lg hover:bg-red-50 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-red-800">Delete Account</div>
                <div className="text-sm text-red-600">Permanently delete your account and data</div>
              </div>
              <i className="fas fa-trash text-red-400"></i>
            </div>
          </button>
        </div>
      </div>
    </div>
  );

  const renderPrivacySettings = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Privacy Controls</h3>
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Profile Visibility</label>
            <select
              value={settings.privacy.profileVisibility}
              onChange={(e) => handleSettingChange('privacy', 'profileVisibility', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {visibilityOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Resume Visibility</label>
            <select
              value={settings.privacy.resumeVisibility}
              onChange={(e) => handleSettingChange('privacy', 'resumeVisibility', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {visibilityOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          <div className="space-y-4">
            <label className="flex items-center justify-between">
              <div>
                <div className="font-medium text-gray-800">Show in Search Results</div>
                <div className="text-sm text-gray-600">Allow employers to find your profile in searches</div>
              </div>
              <input
                type="checkbox"
                checked={settings.privacy.searchAppearance}
                onChange={(e) => handleSettingChange('privacy', 'searchAppearance', e.target.checked)}
                className="rounded border-gray-300 text-blue-500 focus:ring-blue-500"
              />
            </label>

            <label className="flex items-center justify-between">
              <div>
                <div className="font-medium text-gray-800">Data Sharing for Analytics</div>
                <div className="text-sm text-gray-600">Help us improve by sharing anonymous usage data</div>
              </div>
              <input
                type="checkbox"
                checked={settings.privacy.dataSharing}
                onChange={(e) => handleSettingChange('privacy', 'dataSharing', e.target.checked)}
                className="rounded border-gray-300 text-blue-500 focus:ring-blue-500"
              />
            </label>

            <label className="flex items-center justify-between">
              <div>
                <div className="font-medium text-gray-800">Auto-delete Old Data</div>
                <div className="text-sm text-gray-600">Automatically delete data older than 2 years</div>
              </div>
              <input
                type="checkbox"
                checked={settings.privacy.autoDeleteData}
                onChange={(e) => handleSettingChange('privacy', 'autoDeleteData', e.target.checked)}
                className="rounded border-gray-300 text-blue-500 focus:ring-blue-500"
              />
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Email Notifications</h3>
        <div className="space-y-4">
          {Object.entries(settings.notifications.email).map(([key, value]) => (
            <label key={key} className="flex items-center justify-between">
              <div>
                <div className="font-medium text-gray-800 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                <div className="text-sm text-gray-600">
                  {key === 'jobMatches' && 'Get notified about new job matches'}
                  {key === 'applicationUpdates' && 'Receive updates on your applications'}
                  {key === 'interviewInvites' && 'Get notified about interview invitations'}
                  {key === 'messages' && 'Receive message notifications'}
                  {key === 'newsletter' && 'Receive platform updates and tips'}
                </div>
              </div>
              <input
                type="checkbox"
                checked={value}
                onChange={(e) => handleNestedSettingChange('notifications', 'email', key, e.target.checked)}
                className="rounded border-gray-300 text-blue-500 focus:ring-blue-500"
              />
            </label>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Push Notifications</h3>
        <div className="space-y-4">
          {Object.entries(settings.notifications.push).map(([key, value]) => (
            <label key={key} className="flex items-center justify-between">
              <div>
                <div className="font-medium text-gray-800 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                <div className="text-sm text-gray-600">
                  {key === 'jobMatches' && 'Get push notifications for new job matches'}
                  {key === 'applicationUpdates' && 'Push notifications for application updates'}
                  {key === 'interviewInvites' && 'Push notifications for interviews'}
                  {key === 'messages' && 'Push notifications for new messages'}
                </div>
              </div>
              <input
                type="checkbox"
                checked={value}
                onChange={(e) => handleNestedSettingChange('notifications', 'push', key, e.target.checked)}
                className="rounded border-gray-300 text-blue-500 focus:ring-blue-500"
              />
            </label>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">SMS Notifications</h3>
        <div className="space-y-4">
          {Object.entries(settings.notifications.sms).map(([key, value]) => (
            <label key={key} className="flex items-center justify-between">
              <div>
                <div className="font-medium text-gray-800 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                <div className="text-sm text-gray-600">
                  {key === 'interviewReminders' && 'SMS reminders for upcoming interviews'}
                  {key === 'importantUpdates' && 'Important platform updates via SMS'}
                </div>
              </div>
              <input
                type="checkbox"
                checked={value}
                onChange={(e) => handleNestedSettingChange('notifications', 'sms', key, e.target.checked)}
                className="rounded border-gray-300 text-blue-500 focus:ring-blue-500"
              />
            </label>
          ))}
        </div>
      </div>
    </div>
  );

  const renderApplicationSettings = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Application Preferences</h3>
        <div className="space-y-4">
          <label className="flex items-center justify-between">
            <div>
              <div className="font-medium text-gray-800">Auto-save Applications</div>
              <div className="text-sm text-gray-600">Automatically save applications in progress</div>
            </div>
            <input
              type="checkbox"
              checked={settings.application.autoSave}
              onChange={(e) => handleSettingChange('application', 'autoSave', e.target.checked)}
              className="rounded border-gray-300 text-blue-500 focus:ring-blue-500"
            />
          </label>

          <label className="flex items-center justify-between">
            <div>
              <div className="font-medium text-gray-800">Quick Apply</div>
              <div className="text-sm text-gray-600">Apply to jobs with one click using your default resume</div>
            </div>
            <input
              type="checkbox"
              checked={settings.application.quickApply}
              onChange={(e) => handleSettingChange('application', 'quickApply', e.target.checked)}
              className="rounded border-gray-300 text-blue-500 focus:ring-blue-500"
            />
          </label>

          <label className="flex items-center justify-between">
            <div>
              <div className="font-medium text-gray-800">Follow-up Reminders</div>
              <div className="text-sm text-gray-600">Remind you to follow up on applications</div>
            </div>
            <input
              type="checkbox"
              checked={settings.application.followUpReminders}
              onChange={(e) => handleSettingChange('application', 'followUpReminders', e.target.checked)}
              className="rounded border-gray-300 text-blue-500 focus:ring-blue-500"
            />
          </label>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Default Settings</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Default Resume</label>
            <select
              value={settings.application.defaultResume}
              onChange={(e) => handleSettingChange('application', 'defaultResume', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="primary">Primary Resume</option>
              <option value="technical">Technical Resume</option>
              <option value="executive">Executive Resume</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Cover Letter Template</label>
            <select
              value={settings.application.coverLetterTemplate}
              onChange={(e) => handleSettingChange('application', 'coverLetterTemplate', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="professional">Professional</option>
              <option value="creative">Creative</option>
              <option value="minimal">Minimal</option>
              <option value="custom">Custom</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Security Settings</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div>
              <div className="font-medium text-gray-800">Password</div>
              <div className="text-sm text-gray-600">Last changed 3 months ago</div>
            </div>
            <button className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors">
              Change Password
            </button>
          </div>

          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div>
              <div className="font-medium text-gray-800">Two-Factor Authentication</div>
              <div className="text-sm text-gray-600">Add an extra layer of security</div>
            </div>
            <button className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors">
              Enable 2FA
            </button>
          </div>

          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div>
              <div className="font-medium text-gray-800">Login Activity</div>
              <div className="text-sm text-gray-600">Review recent account activity</div>
            </div>
            <button className="text-blue-500 hover:text-blue-700 font-medium">
              View Activity
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Connected Devices</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <i className="fas fa-desktop text-blue-500"></i>
              </div>
              <div>
                <div className="font-medium text-gray-800">MacBook Pro</div>
                <div className="text-sm text-gray-600">Chrome • New York, USA</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-green-600 bg-green-100 px-2 py-1 rounded">Current</span>
              <button className="text-gray-400 hover:text-red-500">
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <i className="fas fa-mobile-alt text-green-500"></i>
              </div>
              <div>
                <div className="font-medium text-gray-800">iPhone 14</div>
                <div className="text-sm text-gray-600">Safari • New York, USA</div>
              </div>
            </div>
            <button className="text-gray-400 hover:text-red-500">
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'profile':
        return renderProfileSettings();
      case 'account':
        return renderAccountSettings();
      case 'privacy':
        return renderPrivacySettings();
      case 'notifications':
        return renderNotificationSettings();
      case 'application':
        return renderApplicationSettings();
      case 'security':
        return renderSecuritySettings();
      default:
        return renderProfileSettings();
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800">Settings</h1>
        <p className="text-gray-400">Manage your account settings and preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar Navigation - 1/4 width */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-md p-6">
            <nav className="space-y-2">
              {settingTabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                    activeTab === tab.id
                      ? 'bg-blue-50 text-blue-600 border border-blue-200'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <i className={`${tab.icon} w-5 text-center`}></i>
                  <span className="font-medium">{tab.name}</span>
                </button>
              ))}
            </nav>

            {/* Save Changes Button */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <button className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 transition-colors font-medium">
                Save Changes
              </button>
              <button className="w-full mt-2 text-gray-600 py-2 rounded-lg hover:bg-gray-100 transition-colors">
                Reset to Defaults
              </button>
            </div>
          </div>
        </div>

        {/* Main Content - 3/4 width */}
        <div className="lg:col-span-3">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default Settings;